import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  HomeIcon,
  TagIcon,
  Squares2X2Icon,
  CubeIcon,
  ArrowRightOnRectangleIcon,
  PlusIcon,
  ArrowUpTrayIcon,
  ChartBarIcon,
  EyeIcon,
  XMarkIcon,
  PhotoIcon,
  CurrencyRupeeIcon,
  IdentificationIcon,
  TagIcon as TagOutlineIcon,
} from '@heroicons/react/24/outline';
import { brandsService, categoriesService, productsService, mediaService, businessTypesService } from '../services/firebase';

const ADMIN_NAV = [
  { id: 'dashboard', label: 'Dashboard', icon: HomeIcon },
  { id: 'brands', label: 'Brands', icon: TagIcon },
  { id: 'categories', label: 'Categories', icon: Squares2X2Icon },
  { id: 'products', label: 'Products', icon: CubeIcon },
];

const SECTION_META = {
  dashboard: { title: 'Dashboard', subtitle: 'Overview of your store performance' },
  brands: { title: 'Brand Management', subtitle: 'Create and manage product brands' },
  categories: { title: 'Category Management', subtitle: 'Organize products into categories' },
  products: { title: 'Product Management', subtitle: 'Manage your product catalog' },
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState(() => {
    // Get the last active section from localStorage, default to 'dashboard'
    return localStorage.getItem('adminActiveSection') || 'dashboard';
  });
  const [showAddBrandModal, setShowAddBrandModal] = useState(false);
  const [editingBrand, setEditingBrand] = useState(null);
  const [brandForm, setBrandForm] = useState({
    name: '',
    status: 'active',
    photo: null
  });
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    status: 'active'
  });
  const [businessTypeForm, setBusinessTypeForm] = useState({
    name: '',
    status: 'active'
  });
  const [productForm, setProductForm] = useState({
    productId: '',
    name: '',
    description: '',
    brandId: '',
    categoryId: '',
    businessTypeIds: [],
    mrp: '',
    offerPrice: '',
    photos: [],
    removeCurrentPhotos: [],
    isPharma: false
  });
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [businessTypes, setBusinessTypes] = useState([]);
  const [products, setProducts] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAddCategoryModal, setShowAddCategoryModal] = useState(false);
  const [showAddBusinessTypeModal, setShowAddBusinessTypeModal] = useState(false);
  const [showAddProductModal, setShowAddProductModal] = useState(false);
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [bulkUploadFile, setBulkUploadFile] = useState(null);
  const [bulkUploadProgress, setBulkUploadProgress] = useState(0);
  const [bulkUploadStatus, setBulkUploadStatus] = useState('');
  const [showBulkUploadBrandModal, setShowBulkUploadBrandModal] = useState(false);
  const [bulkUploadBrandFile, setBulkUploadBrandFile] = useState(null);
  const [bulkUploadBrandProgress, setBulkUploadBrandProgress] = useState(0);
  const [bulkUploadBrandStatus, setBulkUploadBrandStatus] = useState('');
  const [showBulkUploadCategoryModal, setShowBulkUploadCategoryModal] = useState(false);
  const [bulkUploadCategoryFile, setBulkUploadCategoryFile] = useState(null);
  const [bulkUploadCategoryProgress, setBulkUploadCategoryProgress] = useState(0);
  const [bulkUploadCategoryStatus, setBulkUploadCategoryStatus] = useState('');
  
  // Product edit states
  const [editingProduct, setEditingProduct] = useState(null);
  const [showEditProductModal, setShowEditProductModal] = useState(false);
  const [viewingProduct, setViewingProduct] = useState(null);
  const [viewPhotoIndex, setViewPhotoIndex] = useState(0);
  
  // Product filter states
  const [productFilters, setProductFilters] = useState({
    productId: '',
    productName: '',
    brand: '',
    category: '',
    status: ''
  });

  useEffect(() => {
    // Check authentication
    const adminAuthenticated = localStorage.getItem('adminAuthenticated');
    const loginTime = localStorage.getItem('adminLoginTime');
    
    console.log('AdminDashboard: Checking authentication...');
    console.log('adminAuthenticated:', adminAuthenticated);
    console.log('loginTime:', loginTime);
    
    if (adminAuthenticated && loginTime) {
      const now = new Date().getTime();
      const loginTimeStamp = new Date(loginTime).getTime();
      const timeDiff = now - loginTimeStamp;
      const hoursDiff = timeDiff / (1000 * 60 * 60);
      
      console.log('Time difference (hours):', hoursDiff);
      
      if (hoursDiff < 24) {
        console.log('Authentication valid, setting isAuthenticated to true');
        setIsAuthenticated(true);
      } else {
        console.log('Session expired, redirecting to login');
        localStorage.removeItem('adminAuthenticated');
        localStorage.removeItem('adminLoginTime');
        navigate('/admin-login');
      }
          } else {
        console.log('No authentication found, redirecting to login');
        navigate('/admin-login');
      }
      setLoading(false);
    }, [navigate]);

  // Load data when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      loadData();
    }
  }, [isAuthenticated]);

  const handleLogout = () => {
    localStorage.removeItem('adminAuthenticated');
    localStorage.removeItem('adminLoginTime');
    localStorage.removeItem('adminActiveSection');
    navigate('/admin-login');
  };

  const handleSectionChange = (section) => {
    setActiveSection(section);
    localStorage.setItem('adminActiveSection', section);
  };

  const handleBrandFormChange = (e) => {
    const { name, value } = e.target;
    setBrandForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file');
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('File size should be less than 5MB');
        return;
      }
      
      setBrandForm(prev => ({
        ...prev,
        photo: file
      }));
    }
  };

  const handleCategoryFormChange = (e) => {
    const { name, value } = e.target;
    setCategoryForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleProductFormChange = (e) => {
    const { name, value } = e.target;
    setProductForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleProductFilterChange = (e) => {
    const { name, value } = e.target;
    setProductFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const clearProductFilters = () => {
    setProductFilters({
      productId: '',
      productName: '',
      brand: '',
      category: '',
      status: ''
    });
  };

  const getFilteredProducts = () => {
    return products.filter(product => {
      const matchesProductId = !productFilters.productId || 
        product.productId.toLowerCase().includes(productFilters.productId.toLowerCase());
      
      const matchesProductName = !productFilters.productName || 
        product.name.toLowerCase().includes(productFilters.productName.toLowerCase());
      
      const matchesBrand = !productFilters.brand || 
        product.brandName.toLowerCase().includes(productFilters.brand.toLowerCase());
      
      const matchesCategory = !productFilters.category || 
        product.categoryName === productFilters.category;
      
      const matchesStatus = !productFilters.status || 
        (productFilters.status === 'active' && product.isActive) ||
        (productFilters.status === 'inactive' && !product.isActive);
      

      
      return matchesProductId && matchesProductName && matchesBrand && matchesCategory && matchesStatus;
    });
  };

  const handleProductPhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = [];
    
    for (const file of files) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert(`File "${file.name}" is not an image file`);
        continue;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert(`File "${file.name}" is too large. File size should be less than 5MB`);
        continue;
      }
      
      validFiles.push(file);
    }
    
    if (validFiles.length > 0) {
      setProductForm(prev => ({
        ...prev,
        photos: [...prev.photos, ...validFiles]
      }));
    }
  };

  const handleProductPhotoDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const files = Array.from(e.dataTransfer.files || []);
    if (files.length === 0) return;

    const validFiles = [];
    for (const file of files) {
      if (!file.type.startsWith('image/')) continue;
      if (file.size > 5 * 1024 * 1024) continue;
      validFiles.push(file);
    }

    if (validFiles.length > 0) {
      setProductForm(prev => ({
        ...prev,
        photos: [...prev.photos, ...validFiles],
      }));
    }
  };

  const getProductSavingsPreview = () => {
    const mrp = parseFloat(productForm.mrp);
    const offer = parseFloat(productForm.offerPrice);
    if (!mrp || !offer || offer >= mrp) return null;
    return {
      amount: mrp - offer,
      percent: Math.round(((mrp - offer) / mrp) * 100),
    };
  };

  const handleRemoveProductPhoto = (index) => {
    setProductForm(prev => ({
      ...prev,
      photos: prev.photos.filter((_, i) => i !== index)
    }));
  };

  const handleRemoveCurrentPhoto = (index) => {
    setProductForm(prev => ({
      ...prev,
      removeCurrentPhotos: [...(prev.removeCurrentPhotos || []), index]
    }));
  };

  const handleBulkUploadFile = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
        alert('Please select a CSV file');
        return;
      }
      
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        alert('File size should be less than 10MB');
        return;
      }
      
      setBulkUploadFile(file);
    }
  };

  const parseCSV = (csvText) => {
    const lines = csvText.split('\n');
    const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
    const products = [];
    
    for (let i = 1; i < lines.length; i++) {
      if (lines[i].trim()) {
        const values = lines[i].split(',').map(v => v.trim().replace(/"/g, ''));
        const product = {};
        
        headers.forEach((header, index) => {
          product[header] = values[index] || '';
        });
        
        products.push(product);
      }
    }
    
    return products;
  };

  const handleBulkUpload = async (e) => {
    e.preventDefault();
    
    if (!bulkUploadFile) {
      alert('Please select a CSV file');
      return;
    }

    setIsSubmitting(true);
    setBulkUploadProgress(0);
    setBulkUploadStatus('Reading CSV file...');
    
    try {
      const text = await bulkUploadFile.text();
      const products = parseCSV(text);
      
      if (products.length === 0) {
        alert('No products found in CSV file');
        return;
      }
      
      setBulkUploadStatus(`Processing ${products.length} products...`);
      
      let successCount = 0;
      let errorCount = 0;
      
      for (let i = 0; i < products.length; i++) {
        const product = products[i];
        
        try {
          // Validate required fields
          if (!product.ProductID || !product.ProductName || !product.BrandName || !product.CategoryName || !product.MRP || !product.OfferPrice) {
            errorCount++;
            continue;
          }
          
          // Find brand and category IDs
          const brand = brands.find(b => b.name.toLowerCase() === product.BrandName.toLowerCase());
          const category = categories.find(c => 
            c.name.toLowerCase() === product.CategoryName.toLowerCase()
          );
          
          if (!brand || !category) {
            errorCount++;
            continue;
          }
          
          // Parse business types if provided (comma-separated names)
          let businessTypeIds = [];
          let businessTypeNames = [];
          if (product.BusinessType) {
            const businessTypeNamesArray = product.BusinessType.split(',').map(bt => bt.trim());
            businessTypeNames = businessTypeNamesArray;
            businessTypeIds = businessTypes
              .filter(bt => businessTypeNamesArray.some(name => 
                bt.name.toLowerCase() === name.toLowerCase() && bt.isActive
              ))
              .map(bt => bt.id);
          }
          
          // Create product data
          const productData = {
            productId: product.ProductID.trim(),
            name: product.ProductName.trim(),
            description: product.Description?.trim() || '',
            brandId: brand.id,
            brandName: brand.name,
            categoryId: category.id,
            categoryName: category.name,
            businessTypeIds: businessTypeIds,
            businessTypeNames: businessTypeNames,
            mrp: parseFloat(product.MRP),
            offerPrice: parseFloat(product.OfferPrice),
            photo: null, // No photo in bulk upload
            isActive: true,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          };
          
          await productsService.add(productData);
          successCount++;
          
          // Update progress
          const progress = Math.round(((i + 1) / products.length) * 100);
          setBulkUploadProgress(progress);
          setBulkUploadStatus(`Processed ${i + 1} of ${products.length} products...`);
          
        } catch (error) {
          console.error('Error adding product:', product.ProductName, error);
          errorCount++;
        }
      }
      
      // Reload data
      await loadData();
      
      // Show results
      alert(`Bulk upload completed!\n\nSuccessfully added: ${successCount} products\nFailed: ${errorCount} products`);
      
      // Reset form
      setBulkUploadFile(null);
      setBulkUploadProgress(0);
      setBulkUploadStatus('');
      setShowBulkUploadModal(false);
      
    } catch (error) {
      console.error('Error processing CSV:', error);
      alert('Error processing CSV file. Please check the format and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseBulkUploadModal = () => {
    setShowBulkUploadModal(false);
    setBulkUploadFile(null);
    setBulkUploadProgress(0);
    setBulkUploadStatus('');
  };

  const parseBrandCSV = (csvText) => {
    const lines = csvText.trim().split('\n');
    if (lines.length < 2) return [];
    
    const headers = lines[0].split(',').map(h => h.trim());
    const brands = [];
    
    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map(v => v.trim());
      if (values.length === headers.length) {
        const brand = {};
        headers.forEach((header, index) => {
          brand[header] = values[index] || '';
        });
        brands.push(brand);
      }
    }
    
    return brands;
  };

  const handleBulkUploadBrands = async (e) => {
    e.preventDefault();
    
    if (!bulkUploadBrandFile) {
      alert('Please select a CSV file');
      return;
    }

    setIsSubmitting(true);
    setBulkUploadBrandProgress(0);
    setBulkUploadBrandStatus('Reading CSV file...');
    
    try {
      const text = await bulkUploadBrandFile.text();
      const csvBrands = parseBrandCSV(text);
      
      if (csvBrands.length === 0) {
        alert('No brands found in CSV file');
        return;
      }
      
      setBulkUploadBrandStatus(`Processing ${csvBrands.length} brands...`);
      
      console.log('Existing brands in database:', brands);
      
      let successCount = 0;
      let errorCount = 0;
      
      for (let i = 0; i < csvBrands.length; i++) {
        const brand = csvBrands[i];
        
        try {
          console.log('Processing brand:', brand);
          
          // Validate required fields
          if (!brand.BrandName || !brand.Status) {
            console.log('Missing required fields for brand:', brand);
            errorCount++;
            continue;
          }
          
          // Check if brand already exists
          const existingBrand = brands.find(b => b.name && b.name.toLowerCase() === brand.BrandName.toLowerCase());
          if (existingBrand) {
            errorCount++;
            continue;
          }
          
          // Create brand data
          const brandData = {
            name: brand.BrandName.trim(),
            isActive: brand.Status.toLowerCase() === 'active',
            logo: null, // No logo in bulk upload
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          };
          
          await brandsService.add(brandData);
          successCount++;
          
          // Update progress
          const progress = Math.round(((i + 1) / csvBrands.length) * 100);
          setBulkUploadBrandProgress(progress);
          setBulkUploadBrandStatus(`Processed ${i + 1} of ${csvBrands.length} brands...`);
          
        } catch (error) {
          console.error('Error adding brand:', brand.BrandName, error);
          errorCount++;
        }
      }
      
      // Reload data
      await loadData();
      
      // Show results
      alert(`Bulk upload completed!\n\nSuccessfully added: ${successCount} brands\nFailed: ${errorCount} brands`);
      
      // Reset form
      setBulkUploadBrandFile(null);
      setBulkUploadBrandProgress(0);
      setBulkUploadBrandStatus('');
      setShowBulkUploadBrandModal(false);
      
    } catch (error) {
      console.error('Error processing CSV:', error);
      alert('Error processing CSV file. Please check the format and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseBulkUploadBrandModal = () => {
    setShowBulkUploadBrandModal(false);
    setBulkUploadBrandFile(null);
    setBulkUploadBrandProgress(0);
    setBulkUploadBrandStatus('');
  };

  const parseCategoryCSV = (csvText) => {
    const lines = csvText.trim().split('\n');
    if (lines.length < 2) return [];
    
    const headers = lines[0].split(',').map(h => h.trim());
    const categories = [];
    
    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map(v => v.trim());
      if (values.length === headers.length) {
        const category = {};
        headers.forEach((header, index) => {
          category[header] = values[index] || '';
        });
        categories.push(category);
      }
    }
    
    return categories;
  };

  const handleBulkUploadCategories = async (e) => {
    e.preventDefault();
    
    if (!bulkUploadCategoryFile) {
      alert('Please select a CSV file');
      return;
    }

    setIsSubmitting(true);
    setBulkUploadCategoryProgress(0);
    setBulkUploadCategoryStatus('Reading CSV file...');
    
    try {
      const text = await bulkUploadCategoryFile.text();
      const csvCategories = parseCategoryCSV(text);
      
      if (csvCategories.length === 0) {
        alert('No categories found in CSV file');
        return;
      }
      
      setBulkUploadCategoryStatus(`Processing ${csvCategories.length} categories...`);
      
      let successCount = 0;
      let errorCount = 0;
      
      for (let i = 0; i < csvCategories.length; i++) {
        const category = csvCategories[i];
        
        try {
          console.log('Processing category:', category);
          
          // Validate required fields
          if (!category.CategoryName || !category.Status) {
            console.log('Missing required fields for category:', category);
            errorCount++;
            continue;
          }
          
          // Validate category name (only Electronics or Non-Electronics allowed)
          const validCategories = ['Electronics', 'Non-Electronics'];
          if (!validCategories.includes(category.CategoryName)) {
            console.log('Invalid category name:', category.CategoryName);
            errorCount++;
            continue;
          }
          
          // Check if category already exists
          const existingCategory = categories.find(c => 
            c.name && c.name.toLowerCase() === category.CategoryName.toLowerCase()
          );
          if (existingCategory) {
            console.log('Category already exists:', category.CategoryName);
            errorCount++;
            continue;
          }
          
          // Create category data
          const categoryData = {
            name: category.CategoryName.trim(),
            isActive: category.Status.toLowerCase() === 'active',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          };
          
          await categoriesService.add(categoryData);
          successCount++;
          
          // Update progress
          const progress = Math.round(((i + 1) / csvCategories.length) * 100);
          setBulkUploadCategoryProgress(progress);
          setBulkUploadCategoryStatus(`Processed ${i + 1} of ${csvCategories.length} categories...`);
          
        } catch (error) {
          console.error('Error adding category:', category.CategoryName, error);
          errorCount++;
        }
      }
      
      // Reload data
      await loadData();
      
      // Show results
      alert(`Bulk upload completed!\n\nSuccessfully added: ${successCount} categories\nFailed: ${errorCount} categories`);
      
      // Reset form
      setBulkUploadCategoryFile(null);
      setBulkUploadCategoryProgress(0);
      setBulkUploadCategoryStatus('');
      setShowBulkUploadCategoryModal(false);
      
    } catch (error) {
      console.error('Error processing CSV:', error);
      alert('Error processing CSV file. Please check the format and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseBulkUploadCategoryModal = () => {
    setShowBulkUploadCategoryModal(false);
    setBulkUploadCategoryFile(null);
    setBulkUploadCategoryProgress(0);
    setBulkUploadCategoryStatus('');
  };

  const handleAddBrand = async (e) => {
    e.preventDefault();
    
    if (!brandForm.name.trim()) {
      alert('Please enter a brand name');
      return;
    }

    setIsSubmitting(true);
    
    try {
      let photoUrl = editingBrand?.logo || null;
      
      // Upload photo if provided
      if (brandForm.photo) {
        const uploadResult = await mediaService.uploadImage(brandForm.photo, 'brands');
        photoUrl = uploadResult.url; // Extract the URL from the upload result
        console.log('Uploaded photo URL:', photoUrl);
      }
      
      // Create brand data
      const brandData = {
        name: brandForm.name.trim(),
        isActive: brandForm.status === 'active',
        logo: photoUrl,
        updatedAt: new Date().toISOString()
      };
      
      if (editingBrand) {
        // Update existing brand
        brandData.createdAt = editingBrand.createdAt; // Keep original creation date
        await brandsService.update(editingBrand.id, brandData);
        alert('Brand updated successfully!');
      } else {
        // Add new brand
        brandData.createdAt = new Date().toISOString();
        await brandsService.add(brandData);
        alert('Brand added successfully!');
      }
      
      // Reload data
      await loadData();
      
      // Reset form and close modal
      setBrandForm({
        name: '',
        status: 'active',
        photo: null
      });
      setEditingBrand(null);
      setShowAddBrandModal(false);
    } catch (error) {
      console.error('Error saving brand:', error);
      alert('Error saving brand. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseBrandModal = () => {
    setShowAddBrandModal(false);
    setEditingBrand(null);
    setBrandForm({
      name: '',
      status: 'active',
      photo: null
    });
  };

  const handleAddCategory = async (e) => {
    e.preventDefault();
    
    if (!categoryForm.name.trim()) {
      alert('Please enter a category name');
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Create category data
      const categoryData = {
        name: categoryForm.name.trim(),
        isActive: categoryForm.status === 'active',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      
      await categoriesService.add(categoryData);
      alert('Category added successfully!');
      
      // Reload data
      await loadData();
      
      // Reset form and close modal
      setCategoryForm({
        name: '',
        status: 'active'
      });
      setShowAddCategoryModal(false);
    } catch (error) {
      console.error('Error saving category:', error);
      alert('Error saving category. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseCategoryModal = () => {
    setShowAddCategoryModal(false);
    setCategoryForm({
      name: '',
      status: 'active'
    });
  };

  const handleBusinessTypeFormChange = (e) => {
    const { name, value } = e.target;
    setBusinessTypeForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddBusinessType = async (e) => {
    e.preventDefault();
    
    if (!businessTypeForm.name.trim()) {
      alert('Please enter a business type name');
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Create business type data
      const businessTypeData = {
        name: businessTypeForm.name.trim(),
        isActive: businessTypeForm.status === 'active',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      
      await businessTypesService.add(businessTypeData);
      alert('Business Type added successfully!');
      
      // Reload data
      await loadData();
      
      // Reset form and close modal
      setBusinessTypeForm({
        name: '',
        status: 'active'
      });
      setShowAddBusinessTypeModal(false);
    } catch (error) {
      console.error('Error saving business type:', error);
      alert('Error saving business type. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseBusinessTypeModal = () => {
    setShowAddBusinessTypeModal(false);
    setBusinessTypeForm({
      name: '',
      status: 'active'
    });
  };

  const getBusinessTypeRole = (businessType) => {
    const name = businessType.name.trim().toLowerCase();
    if (name === 'all') return 'all';
    if (name.includes('lifestyle')) return 'lifestyle';
    if (name.includes('pharma')) return 'pharma';
    return 'other';
  };

  const handleBusinessTypeCheckboxChange = (businessTypeId, isChecked) => {
    setProductForm(prev => {
      const activeTypes = businessTypes.filter(bt => bt.isActive);
      const byRole = {};
      activeTypes.forEach((bt) => {
        const role = getBusinessTypeRole(bt);
        if (role !== 'other') byRole[role] = bt;
      });

      const nextIds = new Set(prev.businessTypeIds || []);
      const { lifestyle, pharma, all: allType } = byRole;

      if (allType && businessTypeId === allType.id) {
        if (isChecked) {
          [lifestyle, pharma, allType].filter(Boolean).forEach((bt) => nextIds.add(bt.id));
        } else {
          [lifestyle, pharma, allType].filter(Boolean).forEach((bt) => nextIds.delete(bt.id));
        }
      } else {
        if (isChecked) {
          nextIds.add(businessTypeId);
        } else {
          nextIds.delete(businessTypeId);
        }

        if (lifestyle && pharma && allType) {
          const hasLifestyle = nextIds.has(lifestyle.id);
          const hasPharma = nextIds.has(pharma.id);
          if (hasLifestyle && hasPharma) {
            nextIds.add(allType.id);
          } else {
            nextIds.delete(allType.id);
          }
        }
      }

      const businessTypeIds = Array.from(nextIds);
      return {
        ...prev,
        businessTypeIds,
        isPharma: pharma ? businessTypeIds.includes(pharma.id) : prev.isPharma,
      };
    });
  };

  const handleAddProduct = async (e) => {
    e.preventDefault();
    
    if (!productForm.productId.trim()) {
      alert('Please enter a product ID');
      return;
    }
    
    if (!productForm.name.trim()) {
      alert('Please enter a product name');
      return;
    }
    
    if (!productForm.brandId) {
      alert('Please select a brand');
      return;
    }
    
    if (!productForm.categoryId) {
      alert('Please select a category');
      return;
    }
    
    if (!productForm.mrp || !productForm.offerPrice) {
      alert('Please enter both MRP and Offer Price');
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Get the selected brand and category names
      const selectedBrand = brands.find(brand => brand.id === productForm.brandId);
      const selectedCategory = categories.find(category => category.id === productForm.categoryId);
      
      let photoUrls = [];
      
      // Upload photos if provided
      if (productForm.photos && productForm.photos.length > 0) {
        for (const photo of productForm.photos) {
          const uploadResult = await mediaService.uploadImage(photo, 'products');
          photoUrls.push(uploadResult.url);
        }
      }
      
      // Get business type names
      const selectedBusinessTypes = businessTypes.filter(bt => 
        productForm.businessTypeIds && productForm.businessTypeIds.includes(bt.id)
      );
      const businessTypeNames = selectedBusinessTypes.map(bt => bt.name);
      
      // Create product data
      const productData = {
        productId: productForm.productId.trim(),
        name: productForm.name.trim(),
        description: productForm.description.trim(),
        brandId: productForm.brandId,
        brandName: selectedBrand.name,
        categoryId: productForm.categoryId,
        categoryName: selectedCategory.name,
        businessTypeIds: productForm.businessTypeIds || [],
        businessTypeNames: businessTypeNames,
        mrp: parseFloat(productForm.mrp),
        offerPrice: parseFloat(productForm.offerPrice),
        photos: photoUrls,
        photo: photoUrls.length > 0 ? photoUrls[0] : null, // Keep photo for backward compatibility
        isPharma: productForm.isPharma || false,
        isActive: true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      
      await productsService.add(productData);
      alert('Product added successfully!');
      
      // Reload data
      await loadData();
      
      // Reset form and close modal
      setProductForm({
        productId: '',
        name: '',
        description: '',
        brandId: '',
        categoryId: '',
        businessTypeIds: [],
        mrp: '',
        offerPrice: '',
        photos: [],
        isPharma: false
      });
      setShowAddProductModal(false);
    } catch (error) {
      console.error('Error saving product:', error);
      alert('Error saving product. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseProductModal = () => {
    setShowAddProductModal(false);
    setProductForm({
      productId: '',
      name: '',
      description: '',
      brandId: '',
      categoryId: '',
      businessTypeIds: [],
      mrp: '',
      offerPrice: '',
      photos: [],
      isPharma: false
    });
  };

  const handleDeleteCategory = async (categoryId) => {
    if (window.confirm('Are you sure you want to delete this category? This action cannot be undone.')) {
      try {
        await categoriesService.delete(categoryId);
        // Refresh categories data
        const updatedCategories = await categoriesService.getAll();
        setCategories(updatedCategories);
      } catch (error) {
        console.error('Error deleting category:', error);
        alert('Error deleting category. Please try again.');
      }
    }
  };

  const handleDeleteBrand = async (brandId) => {
    if (window.confirm('Are you sure you want to delete this brand? This action cannot be undone.')) {
      try {
        await brandsService.delete(brandId);
        // Refresh brands data
        const updatedBrands = await brandsService.getAll();
        setBrands(updatedBrands);
      } catch (error) {
        console.error('Error deleting brand:', error);
        alert('Error deleting brand. Please try again.');
      }
    }
  };

  const handleDeleteBusinessType = async (businessTypeId) => {
    if (window.confirm('Are you sure you want to delete this business type? This action cannot be undone.')) {
      try {
        await businessTypesService.delete(businessTypeId);
        // Refresh business types data
        const updatedBusinessTypes = await businessTypesService.getAll();
        setBusinessTypes(updatedBusinessTypes);
      } catch (error) {
        console.error('Error deleting business type:', error);
        alert('Error deleting business type. Please try again.');
      }
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (window.confirm('Are you sure you want to delete this product? This action cannot be undone.')) {
      try {
        await productsService.delete(productId);
        // Refresh products data
        const updatedProducts = await productsService.getAll();
        setProducts(updatedProducts);
      } catch (error) {
        console.error('Error deleting product:', error);
        alert('Error deleting product. Please try again.');
      }
    }
  };

  const handleEditBrand = (brand) => {
    setEditingBrand(brand);
    setBrandForm({
      name: brand.name,
      status: brand.isActive ? 'active' : 'inactive',
      photo: null
    });
    setShowAddBrandModal(true);
  };

  const formatProductPrice = (price) => {
    if (price == null || price === '') return '—';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  const getProductPhotos = (product) => {
    const photos = [];
    if (product?.photos?.length) photos.push(...product.photos);
    if (product?.photo && !photos.includes(product.photo)) photos.push(product.photo);
    return photos;
  };

  const handleViewProduct = (product) => {
    setViewingProduct(product);
    setViewPhotoIndex(0);
  };

  const handleCloseViewProduct = () => {
    setViewingProduct(null);
    setViewPhotoIndex(0);
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setProductForm({
      productId: product.productId,
      name: product.name,
      description: product.description || '',
      brandId: product.brandId,
      categoryId: product.categoryId,
      businessTypeIds: product.businessTypeIds || [],
      mrp: product.mrp,
      offerPrice: product.offerPrice,
      photos: [],
      removeCurrentPhotos: [],
      isPharma: product.isPharma || false
    });
    setShowEditProductModal(true);
  };

  const handleCloseEditProductModal = () => {
    setShowEditProductModal(false);
    setEditingProduct(null);
    setProductForm({
      productId: '',
      name: '',
      description: '',
      brandId: '',
      categoryId: '',
      businessTypeIds: [],
      mrp: '',
      offerPrice: '',
      photos: [],
      removeCurrentPhotos: [],
      isPharma: false
    });
  };

  const handleUpdateProduct = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      let photoUrls = editingProduct.photos || [editingProduct.photo].filter(Boolean);
      
      // Remove photos that are marked for removal
      if (productForm.removeCurrentPhotos && productForm.removeCurrentPhotos.length > 0) {
        photoUrls = photoUrls.filter((_, index) => !productForm.removeCurrentPhotos.includes(index));
      }
      
      if (productForm.photos && productForm.photos.length > 0) {
        // Upload new photos
        const newPhotoUrls = [];
        for (const photo of productForm.photos) {
          const uploadResult = await mediaService.uploadImage(photo, 'products');
          newPhotoUrls.push(uploadResult.url);
        }
        photoUrls = newPhotoUrls;
      }
      
      // Get business type names
      const selectedBusinessTypes = businessTypes.filter(bt => 
        productForm.businessTypeIds && productForm.businessTypeIds.includes(bt.id)
      );
      const businessTypeNames = selectedBusinessTypes.map(bt => bt.name);
      
      const updatedProduct = {
        productId: productForm.productId,
        name: productForm.name,
        description: productForm.description,
        brandId: productForm.brandId,
        categoryId: productForm.categoryId,
        businessTypeIds: productForm.businessTypeIds || [],
        businessTypeNames: businessTypeNames,
        mrp: parseFloat(productForm.mrp),
        offerPrice: parseFloat(productForm.offerPrice),
        photos: photoUrls,
        photo: photoUrls.length > 0 ? photoUrls[0] : null, // Keep photo for backward compatibility
        isPharma: productForm.isPharma || false,
        isActive: true,
        updatedAt: new Date()
      };
      
      await productsService.update(editingProduct.id, updatedProduct);
      
      // Update local state
      setProducts(products.map(p => 
        p.id === editingProduct.id 
          ? { ...p, ...updatedProduct, brandName: brands.find(b => b.id === productForm.brandId)?.name, categoryName: categories.find(c => c.id === productForm.categoryId)?.name }
          : p
      ));
      
      handleCloseEditProductModal();
    } catch (error) {
      console.error('Error updating product:', error);
      alert('Failed to update product. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const loadData = async () => {
    try {
      const [brandsData, categoriesData, businessTypesData, productsData] = await Promise.all([
        brandsService.getAll(),
        categoriesService.getAll(),
        businessTypesService.getAll(),
        productsService.getAll()
      ]);
      
      console.log('Loaded brands:', brandsData);
      
      setBrands(brandsData);
      setCategories(categoriesData);
      setBusinessTypes(businessTypesData);
      setProducts(productsData);
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  if (loading) {
    return (
      <div className="admin-shell flex items-center justify-center">
        <div className="admin-card p-10 text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-slate-200 border-t-primary-600 mx-auto" />
          <p className="mt-4 text-sm font-medium text-slate-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  const currentSection = SECTION_META[activeSection];

  return (
    <div className="admin-shell flex min-h-screen">
      {/* Sidebar — desktop */}
      <aside className="admin-sidebar hidden lg:flex lg:w-64 lg:fixed lg:inset-y-0 lg:z-30">
        <div className="flex flex-col flex-1 px-4 py-6">
          <div className="flex items-center gap-3 px-2 mb-8">
            <div className="bg-white rounded-xl p-1.5">
              <img src="/Aalap.png" alt="Aalap" className="h-8 w-auto object-contain" />
            </div>
            <div>
              <p className="font-display font-semibold text-white text-sm">Aalap Admin</p>
              <p className="text-xs text-slate-400">Control Panel</p>
            </div>
          </div>

          <nav className="flex-1 space-y-1">
            {ADMIN_NAV.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => handleSectionChange(id)}
                className={`admin-nav-item ${activeSection === id ? 'admin-nav-item-active' : ''}`}
              >
                <Icon className="h-5 w-5 flex-shrink-0" />
                {label}
              </button>
            ))}
          </nav>

          <button onClick={handleLogout} className="admin-nav-item mt-4 text-red-300 hover:text-red-200 hover:bg-red-950/40">
            <ArrowRightOnRectangleIcon className="h-5 w-5 flex-shrink-0" />
            Logout
          </button>
        </div>
      </aside>

      <div className="flex-1 lg:ml-64 min-w-0">
        {/* Mobile nav */}
        <div className="lg:hidden sticky top-0 z-20 bg-white border-b border-slate-200 px-4 py-3">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <img src="/Aalap.png" alt="Aalap" className="h-7 w-auto" />
              <span className="font-display font-semibold text-slate-900 text-sm">Admin</span>
            </div>
            <button onClick={handleLogout} className="admin-btn-danger py-2 px-3 text-xs">
              Logout
            </button>
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
            {ADMIN_NAV.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => handleSectionChange(id)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors ${
                  activeSection === id
                    ? 'bg-primary-600 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                <Icon className="h-4 w-4" />
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Page header */}
        <header className="bg-white border-b border-slate-200 px-6 py-5">
          <h1 className="admin-section-title">{currentSection.title}</h1>
          <p className="text-sm text-slate-500 mt-1">{currentSection.subtitle}</p>
        </header>

        <main className="p-4 sm:p-6 lg:p-8">
          {activeSection === 'dashboard' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
                <div className="admin-stat-card">
                  <div>
                    <p className="text-sm font-medium text-slate-500">Total Products</p>
                    <p className="text-3xl font-bold text-slate-900 mt-1">{products.length}</p>
                  </div>
                  <div className="flex items-center justify-center w-11 h-11 rounded-xl bg-primary-100">
                    <CubeIcon className="h-5 w-5 text-primary-600" />
                  </div>
                </div>
                <div className="admin-stat-card">
                  <div>
                    <p className="text-sm font-medium text-slate-500">Categories</p>
                    <p className="text-3xl font-bold text-slate-900 mt-1">{categories.length}</p>
                  </div>
                  <div className="flex items-center justify-center w-11 h-11 rounded-xl bg-emerald-100">
                    <Squares2X2Icon className="h-5 w-5 text-emerald-600" />
                  </div>
                </div>
                <div className="admin-stat-card">
                  <div>
                    <p className="text-sm font-medium text-slate-500">Brands</p>
                    <p className="text-3xl font-bold text-slate-900 mt-1">{brands.length}</p>
                  </div>
                  <div className="flex items-center justify-center w-11 h-11 rounded-xl bg-violet-100">
                    <TagIcon className="h-5 w-5 text-violet-600" />
                  </div>
                </div>
                <div className="admin-stat-card">
                  <div>
                    <p className="text-sm font-medium text-slate-500">Active Products</p>
                    <p className="text-3xl font-bold text-slate-900 mt-1">{products.filter(p => p.isActive).length}</p>
                  </div>
                  <div className="flex items-center justify-center w-11 h-11 rounded-xl bg-accent-100">
                    <ChartBarIcon className="h-5 w-5 text-accent-600" />
                  </div>
                </div>
              </div>
              <div className="admin-card p-6">
                <h4 className="font-semibold text-slate-900 mb-2">Quick Actions</h4>
                <div className="flex flex-wrap gap-3">
                  <button onClick={() => handleSectionChange('products')} className="admin-btn-primary">
                    <CubeIcon className="h-4 w-4" />
                    Manage Products
                  </button>
                  <button onClick={() => handleSectionChange('brands')} className="admin-btn-outline">
                    <TagIcon className="h-4 w-4" />
                    Manage Brands
                  </button>
                  <button onClick={() => handleSectionChange('categories')} className="admin-btn-outline">
                    <Squares2X2Icon className="h-4 w-4" />
                    Manage Categories
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeSection === 'brands' && (
            <div className="admin-card p-5 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
                <p className="text-sm text-slate-500">{brands.length} brand{brands.length !== 1 ? 's' : ''} total</p>
                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={() => setShowBulkUploadBrandModal(true)}
                    className="admin-btn-outline"
                  >
                    <ArrowUpTrayIcon className="h-4 w-4" />
                    Bulk Upload
                  </button>
                  <button
                    onClick={() => setShowAddBrandModal(true)}
                    className="admin-btn-success"
                  >
                    <PlusIcon className="h-4 w-4" />
                    Add Brand
                  </button>
                </div>
              </div>
              {brands.length === 0 ? (
                <div className="admin-empty-state">
                  <p className="text-slate-600 mb-4">No brands found yet.</p>
                  <button
                    onClick={() => setShowAddBrandModal(true)}
                    className="admin-btn-success"
                  >
                    <PlusIcon className="h-4 w-4" />
                    Add Your First Brand
                  </button>
                </div>
              ) : (
                <div className="admin-table-wrap">
                  <table className="min-w-full divide-y divide-slate-200">
                    <thead className="bg-slate-50 sticky top-0 z-10">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Brand
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Logo
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {brands.sort((a, b) => a.name.localeCompare(b.name)).map((brand) => (
                        <tr key={brand.id} className="hover:bg-slate-50/80">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">{brand.name}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              brand.isActive 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {brand.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="w-12 h-12 rounded-lg bg-gray-100 flex-shrink-0">
                              {(brand.logo?.url || brand.logoUrl || (typeof brand.logo === 'string' ? brand.logo : null)) ? (
                                <img 
                                  src={brand.logo?.url || brand.logoUrl || brand.logo} 
                                  alt={`${brand.name} logo`}
                                  className="w-full h-full object-cover rounded-lg"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    const fallback = e.target.parentElement.querySelector('.brand-fallback');
                                    if (fallback) {
                                      fallback.style.display = 'flex';
                                    }
                                  }}
                                />
                              ) : null}
                              <div 
                                className={`w-full h-full rounded-lg flex items-center justify-center brand-fallback ${(brand.logo?.url || brand.logoUrl || (typeof brand.logo === 'string' ? brand.logo : null)) ? 'hidden' : 'flex'}`}
                              >
                                <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                </svg>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex space-x-2">
                              <button
                                onClick={() => handleEditBrand(brand)}
                                className="admin-btn-icon-edit"
                                title="Edit brand"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                              </button>
                              <button
                                onClick={() => handleDeleteBrand(brand.id)}
                                className="admin-btn-icon-delete"
                                title="Delete brand"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {activeSection === 'categories' && (
            <div className="admin-card p-5 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
                <p className="text-sm text-slate-500">{categories.length} categor{categories.length !== 1 ? 'ies' : 'y'} total</p>
                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={() => setShowBulkUploadCategoryModal(true)}
                    className="admin-btn-outline"
                  >
                    <ArrowUpTrayIcon className="h-4 w-4" />
                    Bulk Upload
                  </button>
                  <button
                    onClick={() => setShowAddCategoryModal(true)}
                    className="admin-btn-primary"
                  >
                    <PlusIcon className="h-4 w-4" />
                    Add Category
                  </button>
                </div>
              </div>
              {categories.length === 0 ? (
                <div className="admin-empty-state">
                  <p className="text-slate-600 mb-4">No categories found yet.</p>
                  <button
                    onClick={() => setShowAddCategoryModal(true)}
                    className="admin-btn-primary"
                  >
                    <PlusIcon className="h-4 w-4" />
                    Add Your First Category
                  </button>
                </div>
              ) : (
                <div className="admin-table-wrap">
                  <table className="min-w-full divide-y divide-slate-200">
                    <thead className="bg-slate-50 sticky top-0 z-10">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Category
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {categories.map((category) => (
                        <tr key={category.id} className="hover:bg-slate-50/80">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">{category.name}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              category.isActive 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {category.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex space-x-2">
                              <button
                                className="admin-btn-icon-edit"
                                title="Edit category"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                              </button>
                              <button
                                onClick={() => handleDeleteCategory(category.id)}
                                className="admin-btn-icon-delete"
                                title="Delete category"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {activeSection === 'products' && (
            <div className="space-y-6">
              <div className="admin-card p-5 sm:p-6">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
                  <div className="flex flex-wrap items-center gap-3 text-sm text-slate-500">
                    <span>
                      Total: <span className="font-semibold text-slate-900">{products.length}</span>
                    </span>
                    {getFilteredProducts().length !== products.length && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold">
                        Showing {getFilteredProducts().length}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-3">
                    <button
                      onClick={() => setShowBulkUploadModal(true)}
                      className="admin-btn-outline"
                    >
                      <ArrowUpTrayIcon className="h-4 w-4" />
                      Bulk Upload
                    </button>
                    <button
                      onClick={() => setShowAddProductModal(true)}
                      className="admin-btn-primary"
                    >
                      <PlusIcon className="h-4 w-4" />
                      Add Product
                    </button>
                  </div>
                </div>
              </div>

              {/* Product Filters */}
              <div className="admin-filter-panel">
                <div className="flex justify-between items-center mb-4">
                  <h4 className="text-sm font-semibold text-slate-900">Filter Products</h4>
                  <button
                    onClick={clearProductFilters}
                    className="text-sm font-medium text-primary-600 hover:text-primary-800 transition-colors"
                  >
                    Clear All
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">
                      Product ID
                    </label>
                    <input
                      type="text"
                      name="productId"
                      value={productFilters.productId}
                      onChange={handleProductFilterChange}
                      placeholder="Filter by Product ID"
                      className="admin-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">
                      Product Name
                    </label>
                    <input
                      type="text"
                      name="productName"
                      value={productFilters.productName}
                      onChange={handleProductFilterChange}
                      placeholder="Filter by Product Name"
                      className="admin-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">
                      Brand
                    </label>
                    <input
                      type="text"
                      name="brand"
                      value={productFilters.brand}
                      onChange={handleProductFilterChange}
                      placeholder="Filter by Brand"
                      className="admin-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">
                      Category
                    </label>
                    <select
                      name="category"
                      value={productFilters.category}
                      onChange={handleProductFilterChange}
                      className="admin-input"
                    >
                      <option value="">All Categories</option>
                      <option value="Electronics">Electronics</option>
                      <option value="Non-Electronics">Non-Electronics</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">
                      Status
                    </label>
                    <select
                      name="status"
                      value={productFilters.status}
                      onChange={handleProductFilterChange}
                      className="admin-input"
                    >
                      <option value="">All Status</option>
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>
                </div>
              </div>
              {getFilteredProducts().length === 0 ? (
                <div className="admin-empty-state">
                  <p className="text-slate-600 mb-4">
                    {products.length === 0 ? 'No products found yet.' : 'No products match the current filters.'}
                  </p>
                  {products.length === 0 ? (
                    <button
                      onClick={() => setShowAddProductModal(true)}
                      className="admin-btn-primary"
                    >
                      <PlusIcon className="h-4 w-4" />
                      Add Your First Product
                    </button>
                  ) : (
                    <button
                      onClick={clearProductFilters}
                      className="admin-btn-secondary"
                    >
                      Clear Filters
                    </button>
                  )}
                </div>
              ) : (
                <div className="admin-card p-0 overflow-hidden">
                <div className="admin-table-wrap border-0 rounded-none max-h-[calc(100vh-380px)]">
                  <table className="w-full table-fixed divide-y divide-slate-200">
                    <thead className="bg-slate-50 sticky top-0 z-10">
                      <tr>
                        <th className="w-24 px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Product ID
                        </th>
                        <th className="w-44 px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Product
                        </th>
                        <th className="w-24 px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Brand
                        </th>
                        <th className="w-24 px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Category
                        </th>
                        <th className="w-20 px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          MRP
                        </th>
                        <th className="w-24 px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Offer Price
                        </th>
                        <th className="w-20 px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="w-28 px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {getFilteredProducts().sort((a, b) => a.productId.localeCompare(b.productId)).map((product) => (
                        <tr key={product.id} className="hover:bg-slate-50/80">
                          <td className="px-3 py-3">
                            <div className="text-sm font-medium text-gray-900 truncate">{product.productId}</div>
                          </td>
                          <td className="px-3 py-3">
                            <div className="flex items-start min-w-0">
                              <div className="w-8 h-8 rounded-lg bg-gray-100 flex-shrink-0">
                                {product.photo ? (
                                  <img 
                                    src={product.photo} 
                                    alt={product.name}
                                    className="w-full h-full object-cover rounded-lg"
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                      const fallback = e.target.parentElement.querySelector('.product-fallback');
                                      if (fallback) {
                                        fallback.style.display = 'flex';
                                      }
                                    }}
                                  />
                                ) : null}
                                <div 
                                  className={`w-full h-full rounded-lg flex items-center justify-center product-fallback ${product.photo ? 'hidden' : 'flex'}`}
                                >
                                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                                  </svg>
                                </div>
                              </div>
                              <div className="ml-2 min-w-0 flex-1">
                                <div className="text-sm font-medium text-gray-900 truncate" title={product.name}>{product.name}</div>
                                <div className="text-xs text-gray-500 line-clamp-2" title={product.description}>{product.description}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-3 py-3">
                            <div className="text-sm text-gray-900 truncate">{product.brandName}</div>
                          </td>
                          <td className="px-3 py-3">
                            <div className="text-sm text-gray-900 truncate">{product.categoryName}</div>
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap">
                            <div className="text-sm text-gray-900">₹{product.mrp}</div>
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap">
                            <div className="text-sm text-gray-900">₹{product.offerPrice}</div>
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              product.isActive 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {product.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap text-sm font-medium">
                            <div className="flex space-x-1">
                              <button
                                type="button"
                                onClick={() => handleViewProduct(product)}
                                className="admin-btn-icon-view"
                                title="View product"
                              >
                                <EyeIcon className="w-4 h-4" />
                              </button>
                              <button
                                type="button"
                                onClick={() => handleEditProduct(product)}
                                className="admin-btn-icon-edit"
                                title="Edit product"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDeleteProduct(product.id)}
                                className="admin-btn-icon-delete"
                                title="Delete product"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                </div>
              )}
            </div>
          )}
      </main>

      {/* Add Brand Modal */}
      {showAddBrandModal && (
        <div className="admin-modal-overlay">
          <div className="admin-modal max-w-md">
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                {editingBrand ? 'Edit Brand' : 'Add New Brand'}
              </h3>
              <button
                onClick={handleCloseBrandModal}
                className="text-gray-400 hover:text-gray-600 text-xl font-bold"
              >
                ×
              </button>
            </div>
            
            <form onSubmit={handleAddBrand} className="p-6">
              <div className="space-y-4">
                <div>
                  <label htmlFor="brandName" className="block text-sm font-medium text-gray-700 mb-1">
                    Brand Name *
                  </label>
                  <input
                    type="text"
                    id="brandName"
                    name="name"
                    value={brandForm.name}
                    onChange={handleBrandFormChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="Enter brand name"
                    required
                  />
                </div>
                
                <div>
                  <label htmlFor="brandStatus" className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    id="brandStatus"
                    name="status"
                    value={brandForm.status}
                    onChange={handleBrandFormChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
                
                <div>
                  <label htmlFor="brandPhoto" className="block text-sm font-medium text-gray-700 mb-1">
                    Brand Photo
                  </label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-green-400 transition-colors">
                    <input
                      type="file"
                      id="brandPhoto"
                      accept="image/*"
                      onChange={handlePhotoUpload}
                      className="hidden"
                    />
                    <label htmlFor="brandPhoto" className="cursor-pointer">
                      {brandForm.photo ? (
                        <div>
                          <div className="w-16 h-16 mx-auto mb-2 rounded-lg overflow-hidden bg-gray-100">
                            <img
                              src={URL.createObjectURL(brandForm.photo)}
                              alt="Brand preview"
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <p className="text-sm text-green-600 font-medium">{brandForm.photo.name}</p>
                          <p className="text-xs text-gray-500">Click to change</p>
                        </div>
                      ) : (
                        <div>
                          <div className="w-16 h-16 mx-auto mb-2 rounded-lg bg-gray-100 flex items-center justify-center">
                            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                            </svg>
                          </div>
                          <p className="text-sm text-gray-600">Click to upload brand photo</p>
                          <p className="text-xs text-gray-500">PNG, JPG up to 5MB</p>
                        </div>
                      )}
                    </label>
                  </div>
                </div>
              </div>
              
              <div className="flex space-x-3 mt-6">
                <button
                  type="button"
                  onClick={handleCloseBrandModal}
                  className="flex-1 admin-btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 admin-btn-success disabled:opacity-60"
                >
                  {isSubmitting ? (editingBrand ? 'Updating...' : 'Adding...') : (editingBrand ? 'Update Brand' : 'Add Brand')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Category Modal */}
      {showAddCategoryModal && (
        <div className="admin-modal-overlay">
          <div className="admin-modal max-w-md">
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                Add New Category
              </h3>
              <button
                onClick={handleCloseCategoryModal}
                className="text-gray-400 hover:text-gray-600 text-xl font-bold"
              >
                ×
              </button>
            </div>
            
            <form onSubmit={handleAddCategory} className="p-6">
              <div className="space-y-4">
                <div>
                  <label htmlFor="categoryName" className="block text-sm font-medium text-gray-700 mb-1">
                    Category Name *
                  </label>
                  <select
                    id="categoryName"
                    name="name"
                    value={categoryForm.name}
                    onChange={handleCategoryFormChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    required
                  >
                    <option value="">Select a category</option>
                    <option value="Electronics">Electronics</option>
                    <option value="Non-Electronics">Non-Electronics</option>
                  </select>
                </div>
                
                <div>
                  <label htmlFor="categoryStatus" className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    id="categoryStatus"
                    name="status"
                    value={categoryForm.status}
                    onChange={handleCategoryFormChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={handleCloseCategoryModal}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="admin-btn-primary disabled:opacity-60"
                >
                  {isSubmitting ? 'Adding...' : 'Add Category'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Product Modal */}
      {showAddProductModal && (
        <div className="admin-modal-overlay p-3 sm:p-4">
          <div className="admin-modal w-[96vw] max-w-5xl h-[94vh] flex flex-col overflow-hidden">
            <div className="flex justify-between items-start gap-4 px-6 py-4 border-b border-slate-200 shrink-0">
              <div>
                <h3 className="text-lg font-display font-semibold text-slate-900">Add New Product</h3>
                <p className="text-sm text-slate-500 mt-0.5">Create a new listing for your product catalog</p>
              </div>
              <button
                type="button"
                onClick={handleCloseProductModal}
                className="admin-btn-icon text-slate-400 hover:text-slate-600 hover:bg-slate-100 shrink-0"
                aria-label="Close"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleAddProduct} className="flex flex-col flex-1 min-h-0">
              <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
                {/* Basic Information */}
                <section className="admin-form-section">
                  <h4 className="admin-form-section-title">
                    <IdentificationIcon className="w-4 h-4 text-primary-600" />
                    Basic Information
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="productId" className="admin-form-label">
                        Product ID <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="productId"
                        name="productId"
                        value={productForm.productId}
                        onChange={handleProductFormChange}
                        className="admin-input"
                        placeholder="e.g. PRD-001"
                        required
                      />
                      <p className="admin-form-hint">Unique identifier used in inventory and search</p>
                    </div>

                    <div>
                      <label htmlFor="productName" className="admin-form-label">
                        Product Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="productName"
                        name="name"
                        value={productForm.name}
                        onChange={handleProductFormChange}
                        className="admin-input"
                        placeholder="Enter product name"
                        required
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label htmlFor="productDescription" className="admin-form-label">
                        Description
                      </label>
                      <textarea
                        id="productDescription"
                        name="description"
                        value={productForm.description}
                        onChange={handleProductFormChange}
                        rows="3"
                        className="admin-input resize-y min-h-[88px]"
                        placeholder="Describe key features, specs, or usage details..."
                      />
                    </div>
                  </div>
                </section>

                {/* Classification */}
                <section className="admin-form-section">
                  <h4 className="admin-form-section-title">
                    <TagOutlineIcon className="w-4 h-4 text-primary-600" />
                    Classification
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="productBrand" className="admin-form-label">
                        Brand <span className="text-red-500">*</span>
                      </label>
                      <select
                        id="productBrand"
                        name="brandId"
                        value={productForm.brandId}
                        onChange={handleProductFormChange}
                        className="admin-input"
                        required
                      >
                        <option value="">Select a brand</option>
                        {brands.filter(brand => brand.isActive).map((brand) => (
                          <option key={brand.id} value={brand.id}>
                            {brand.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label htmlFor="productCategory" className="admin-form-label">
                        Category <span className="text-red-500">*</span>
                      </label>
                      <select
                        id="productCategory"
                        name="categoryId"
                        value={productForm.categoryId}
                        onChange={handleProductFormChange}
                        className="admin-input"
                        required
                      >
                        <option value="">Select a category</option>
                        {categories
                          .filter(category => category.isActive)
                          .map((category) => (
                            <option key={category.id} value={category.id}>
                              {category.name}
                            </option>
                          ))}
                      </select>
                    </div>

                    {businessTypes.filter(bt => bt.isActive).length > 0 && (
                      <div className="md:col-span-2">
                        <label className="admin-form-label">Business Types</label>
                        <p className="admin-form-hint mb-3">
                          Select all that apply. Choosing both Lifestyle &amp; Personal and Pharma &amp; FMCG will automatically select All.
                        </p>
                        <div className="space-y-3">
                          {[...businessTypes.filter(bt => bt.isActive)]
                            .sort((a, b) => {
                              const order = { lifestyle: 0, pharma: 1, all: 2, other: 3 };
                              return order[getBusinessTypeRole(a)] - order[getBusinessTypeRole(b)];
                            })
                            .map((bt) => {
                              const isSelected = productForm.businessTypeIds?.includes(bt.id);
                              const role = getBusinessTypeRole(bt);
                              const hint = role === 'all'
                                ? 'Applies to all business categories'
                                : role === 'lifestyle'
                                  ? 'Corporate gifts, electronics, and lifestyle essentials'
                                  : role === 'pharma'
                                    ? 'Pharmaceutical, healthcare, and FMCG items'
                                    : null;

                              return (
                                <label key={bt.id} className="admin-form-toggle">
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={(e) => handleBusinessTypeCheckboxChange(bt.id, e.target.checked)}
                                    className="w-5 h-5 mt-0.5 text-primary-600 border-slate-300 rounded focus:ring-primary-500 shrink-0"
                                  />
                                  <div>
                                    <span className="text-sm font-medium text-slate-800">{bt.name}</span>
                                    {hint && (
                                      <p className="text-xs text-slate-500 mt-0.5">{hint}</p>
                                    )}
                                  </div>
                                </label>
                              );
                            })}
                        </div>
                      </div>
                    )}
                  </div>
                </section>

                {/* Pricing */}
                <section className="admin-form-section">
                  <h4 className="admin-form-section-title">
                    <CurrencyRupeeIcon className="w-4 h-4 text-primary-600" />
                    Pricing
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="productMrp" className="admin-form-label">
                        MRP <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 text-sm font-medium pointer-events-none">₹</span>
                        <input
                          type="number"
                          id="productMrp"
                          name="mrp"
                          value={productForm.mrp}
                          onChange={handleProductFormChange}
                          className="admin-input pl-8"
                          placeholder="0.00"
                          min="0"
                          step="0.01"
                          required
                        />
                      </div>
                      <p className="admin-form-hint">Maximum retail price</p>
                    </div>

                    <div>
                      <label htmlFor="productOfferPrice" className="admin-form-label">
                        Offer Price <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 text-sm font-medium pointer-events-none">₹</span>
                        <input
                          type="number"
                          id="productOfferPrice"
                          name="offerPrice"
                          value={productForm.offerPrice}
                          onChange={handleProductFormChange}
                          className="admin-input pl-8"
                          placeholder="0.00"
                          min="0"
                          step="0.01"
                          required
                        />
                      </div>
                      <p className="admin-form-hint">Selling price shown to customers</p>
                    </div>
                  </div>

                  {(() => {
                    const savings = getProductSavingsPreview();
                    if (!savings) return null;
                    return (
                      <div className="admin-form-savings mt-4">
                        <span className="font-semibold">Customer saves ₹{savings.amount.toFixed(2)}</span>
                        <span className="text-emerald-600">({savings.percent}% off MRP)</span>
                      </div>
                    );
                  })()}
                </section>

                {/* Photos */}
                <section className="admin-form-section">
                  <h4 className="admin-form-section-title">
                    <PhotoIcon className="w-4 h-4 text-primary-600" />
                    Product Photos
                  </h4>
                  <div
                    className="admin-form-upload"
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={handleProductPhotoDrop}
                  >
                    <input
                      type="file"
                      id="productPhoto"
                      accept="image/*"
                      multiple
                      onChange={handleProductPhotoUpload}
                      className="hidden"
                    />
                    <label htmlFor="productPhoto" className="cursor-pointer block">
                      <div className="w-14 h-14 mx-auto mb-3 rounded-xl bg-primary-50 flex items-center justify-center">
                        <PhotoIcon className="w-7 h-7 text-primary-500" />
                      </div>
                      <p className="text-sm font-medium text-slate-700">
                        Click to upload or drag & drop
                      </p>
                      <p className="text-xs text-slate-500 mt-1">PNG, JPG up to 5 MB each · Multiple files allowed</p>
                    </label>
                  </div>

                  {productForm.photos && productForm.photos.length > 0 && (
                    <div className="mt-4">
                      <p className="text-sm font-medium text-slate-700 mb-3">
                        {productForm.photos.length} photo{productForm.photos.length !== 1 ? 's' : ''} ready to upload
                      </p>
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                        {productForm.photos.map((photo, index) => (
                          <div key={index} className="relative group">
                            <div className="w-full aspect-square rounded-xl overflow-hidden bg-slate-100 border border-slate-200">
                              <img
                                src={URL.createObjectURL(photo)}
                                alt={`Product photo ${index + 1}`}
                                className="w-full h-full object-cover"
                              />
                            </div>
                            <button
                              type="button"
                              onClick={() => handleRemoveProductPhoto(index)}
                              className="absolute top-1.5 right-1.5 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-sm hover:bg-red-600"
                              aria-label={`Remove photo ${index + 1}`}
                            >
                              <XMarkIcon className="w-3.5 h-3.5" />
                            </button>
                            {index === 0 && (
                              <span className="absolute bottom-1.5 left-1.5 px-2 py-0.5 bg-slate-900/75 text-white text-[10px] font-medium rounded-md">
                                Cover
                              </span>
                            )}
                            <p className="text-xs text-slate-500 mt-1 truncate">{photo.name}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </section>
              </div>

              <div className="admin-form-modal-footer">
                <p className="text-xs text-slate-500 hidden sm:block">
                  <span className="text-red-500">*</span> Required fields
                </p>
                <div className="flex gap-3 w-full sm:w-auto">
                  <button
                    type="button"
                    onClick={handleCloseProductModal}
                    className="admin-btn-outline flex-1 sm:flex-none"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="admin-btn-primary flex-1 sm:flex-none"
                  >
                    {isSubmitting ? (
                      <>
                        <span className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Adding...
                      </>
                    ) : (
                      <>
                        <PlusIcon className="w-4 h-4" />
                        Add Product
                      </>
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bulk Upload Brands Modal */}
      {showBulkUploadBrandModal && (
        <div className="admin-modal-overlay">
          <div className="admin-modal max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                Bulk Upload Brands
              </h3>
              <button
                onClick={handleCloseBulkUploadBrandModal}
                className="text-gray-400 hover:text-gray-600 text-xl font-bold"
              >
                ×
              </button>
            </div>
            
            <div className="p-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Upload Section */}
                <div>
                  <h4 className="text-md font-semibold text-gray-900 mb-4">Upload CSV File</h4>
                  
                  <form onSubmit={handleBulkUploadBrands}>
                    <div className="space-y-4">
                      <div>
                        <label htmlFor="bulkUploadBrandFile" className="block text-sm font-medium text-gray-700 mb-1">
                          Select CSV File *
                        </label>
                        <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-blue-400 transition-colors">
                          <input
                            type="file"
                            id="bulkUploadBrandFile"
                            accept=".csv"
                            onChange={(e) => setBulkUploadBrandFile(e.target.files[0])}
                            className="hidden"
                          />
                          <label htmlFor="bulkUploadBrandFile" className="cursor-pointer">
                            {bulkUploadBrandFile ? (
                              <div>
                                <div className="w-16 h-16 mx-auto mb-2 rounded-lg bg-blue-100 flex items-center justify-center">
                                  <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                  </svg>
                                </div>
                                <p className="text-sm text-blue-600 font-medium">{bulkUploadBrandFile.name}</p>
                                <p className="text-xs text-gray-500">Click to change</p>
                              </div>
                            ) : (
                              <div>
                                <div className="w-16 h-16 mx-auto mb-2 rounded-lg bg-gray-100 flex items-center justify-center">
                                  <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                  </svg>
                                </div>
                                <p className="text-sm text-gray-600">Click to upload CSV file</p>
                                <p className="text-xs text-gray-500">Max 10MB</p>
                              </div>
                            )}
                          </label>
                        </div>
                      </div>
                      
                      {bulkUploadBrandStatus && (
                        <div className="space-y-2">
                          <div className="flex justify-between text-sm text-gray-600">
                            <span>{bulkUploadBrandStatus}</span>
                            <span>{bulkUploadBrandProgress}%</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                              style={{ width: `${bulkUploadBrandProgress}%` }}
                            ></div>
                          </div>
                        </div>
                      )}
                      
                      <div className="flex justify-end space-x-3">
                        <button
                          type="button"
                          onClick={handleCloseBulkUploadBrandModal}
                          className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          disabled={isSubmitting || !bulkUploadBrandFile}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          {isSubmitting ? 'Uploading...' : 'Upload Brands'}
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
                
                {/* CSV Format Section */}
                <div>
                  <h4 className="text-md font-semibold text-gray-900 mb-4">CSV Format</h4>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-600 mb-3">
                      Your CSV file should have the following columns:
                    </p>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="font-medium">BrandName</span>
                        <span className="text-gray-500">Required</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="font-medium">Status</span>
                        <span className="text-gray-500">Required (active/inactive)</span>
                      </div>
                    </div>
                    
                    <div className="mt-4">
                      <p className="text-sm font-medium text-gray-900 mb-2">Example:</p>
                      <div className="bg-white border rounded p-3 text-xs font-mono">
                        BrandName,Status<br/>
                        Nike,active<br/>
                        Adidas,active<br/>
                        Puma,inactive
                      </div>
                    </div>
                    
                    <div className="mt-4">
                      <button
                        type="button"
                        onClick={() => {
                          const csvContent = 'BrandName,Status\nNike,active\nAdidas,active\nPuma,inactive';
                          const blob = new Blob([csvContent], { type: 'text/csv' });
                          const url = window.URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = 'brands_template.csv';
                          a.click();
                          window.URL.revokeObjectURL(url);
                        }}
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                      >
                        Download Template
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Upload Categories Modal */}
      {showBulkUploadCategoryModal && (
        <div className="admin-modal-overlay">
          <div className="admin-modal max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                Bulk Upload Categories
              </h3>
              <button
                onClick={handleCloseBulkUploadCategoryModal}
                className="text-gray-400 hover:text-gray-600 text-xl font-bold"
              >
                ×
              </button>
            </div>
            
            <div className="p-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Upload Section */}
                <div>
                  <h4 className="text-md font-semibold text-gray-900 mb-4">Upload CSV File</h4>
                  
                  <form onSubmit={handleBulkUploadCategories}>
                    <div className="space-y-4">
                      <div>
                        <label htmlFor="bulkUploadCategoryFile" className="block text-sm font-medium text-gray-700 mb-1">
                          Select CSV File *
                        </label>
                        <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-blue-400 transition-colors">
                          <input
                            type="file"
                            id="bulkUploadCategoryFile"
                            accept=".csv"
                            onChange={(e) => setBulkUploadCategoryFile(e.target.files[0])}
                            className="hidden"
                          />
                          <label htmlFor="bulkUploadCategoryFile" className="cursor-pointer">
                            {bulkUploadCategoryFile ? (
                              <div>
                                <div className="w-16 h-16 mx-auto mb-2 rounded-lg bg-blue-100 flex items-center justify-center">
                                  <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                  </svg>
                                </div>
                                <p className="text-sm text-blue-600 font-medium">{bulkUploadCategoryFile.name}</p>
                                <p className="text-xs text-gray-500">Click to change</p>
                              </div>
                            ) : (
                              <div>
                                <div className="w-16 h-16 mx-auto mb-2 rounded-lg bg-gray-100 flex items-center justify-center">
                                  <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                  </svg>
                                </div>
                                <p className="text-sm text-gray-600">Click to upload CSV file</p>
                                <p className="text-xs text-gray-500">Max 10MB</p>
                              </div>
                            )}
                          </label>
                        </div>
                      </div>
                      
                      {bulkUploadCategoryStatus && (
                        <div className="space-y-2">
                          <div className="flex justify-between text-sm text-gray-600">
                            <span>{bulkUploadCategoryStatus}</span>
                            <span>{bulkUploadCategoryProgress}%</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                              style={{ width: `${bulkUploadCategoryProgress}%` }}
                            ></div>
                          </div>
                        </div>
                      )}
                      
                      <div className="flex justify-end space-x-3">
                        <button
                          type="button"
                          onClick={handleCloseBulkUploadCategoryModal}
                          className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          disabled={isSubmitting || !bulkUploadCategoryFile}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          {isSubmitting ? 'Uploading...' : 'Upload Categories'}
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
                
                {/* CSV Format Section */}
                <div>
                  <h4 className="text-md font-semibold text-gray-900 mb-4">CSV Format</h4>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-600 mb-3">
                      Your CSV file should have the following columns:
                    </p>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="font-medium">CategoryName</span>
                        <span className="text-gray-500">Required (Electronics or Non-Electronics only)</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="font-medium">Status</span>
                        <span className="text-gray-500">Required (active/inactive)</span>
                      </div>
                    </div>
                    
                    <div className="mt-4">
                      <p className="text-sm font-medium text-gray-900 mb-2">Example:</p>
                      <div className="bg-white border rounded p-3 text-xs font-mono">
                        CategoryName,Status<br/>
                        Electronics,active<br/>
                        Non-Electronics,active
                      </div>
                    </div>
                    
                    <div className="mt-4">
                      <p className="text-sm text-gray-600">
                        <strong>Note:</strong> Only "Electronics" and "Non-Electronics" categories are allowed. 
                        Duplicate categories will be skipped.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Upload Modal */}
      {showBulkUploadModal && (
        <div className="admin-modal-overlay">
          <div className="admin-modal max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                Bulk Upload Products
              </h3>
              <button
                onClick={handleCloseBulkUploadModal}
                className="text-gray-400 hover:text-gray-600 text-xl font-bold"
              >
                ×
              </button>
            </div>
            
            <div className="p-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Upload Section */}
                <div>
                  <h4 className="text-md font-semibold text-gray-900 mb-4">Upload CSV File</h4>
                  
                  <form onSubmit={handleBulkUpload}>
                    <div className="space-y-4">
                      <div>
                        <label htmlFor="bulkUploadFile" className="block text-sm font-medium text-gray-700 mb-1">
                          Select CSV File *
                        </label>
                        <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-blue-400 transition-colors">
                          <input
                            type="file"
                            id="bulkUploadFile"
                            accept=".csv"
                            onChange={handleBulkUploadFile}
                            className="hidden"
                          />
                          <label htmlFor="bulkUploadFile" className="cursor-pointer">
                            {bulkUploadFile ? (
                              <div>
                                <div className="w-16 h-16 mx-auto mb-2 rounded-lg bg-blue-100 flex items-center justify-center">
                                  <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                  </svg>
                                </div>
                                <p className="text-sm text-blue-600 font-medium">{bulkUploadFile.name}</p>
                                <p className="text-xs text-gray-500">Click to change</p>
                              </div>
                            ) : (
                              <div>
                                <div className="w-16 h-16 mx-auto mb-2 rounded-lg bg-gray-100 flex items-center justify-center">
                                  <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                  </svg>
                                </div>
                                <p className="text-sm text-gray-600">Click to upload CSV file</p>
                                <p className="text-xs text-gray-500">Max 10MB</p>
                              </div>
                            )}
                          </label>
                        </div>
                      </div>
                      
                      {bulkUploadStatus && (
                        <div className="space-y-2">
                          <div className="flex justify-between text-sm text-gray-600">
                            <span>{bulkUploadStatus}</span>
                            <span>{bulkUploadProgress}%</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                              style={{ width: `${bulkUploadProgress}%` }}
                            ></div>
                          </div>
                        </div>
                      )}
                      
                      <div className="flex justify-end space-x-3">
                        <button
                          type="button"
                          onClick={handleCloseBulkUploadModal}
                          className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          disabled={isSubmitting || !bulkUploadFile}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          {isSubmitting ? 'Uploading...' : 'Upload Products'}
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
                
                {/* CSV Format Section */}
                <div>
                  <h4 className="text-md font-semibold text-gray-900 mb-4">CSV Format</h4>
                  
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-600 mb-3">
                      Your CSV file should have the following columns (headers are case-sensitive):
                    </p>
                    
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="font-medium">ProductID</span>
                        <span className="text-gray-500">Required</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="font-medium">ProductName</span>
                        <span className="text-gray-500">Required</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="font-medium">Description</span>
                        <span className="text-gray-500">Optional</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="font-medium">BrandName</span>
                        <span className="text-gray-500">Required</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="font-medium">CategoryName</span>
                        <span className="text-gray-500">Required</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="font-medium">MRP</span>
                        <span className="text-gray-500">Required</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="font-medium">OfferPrice</span>
                        <span className="text-gray-500">Required</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="font-medium">BusinessType</span>
                        <span className="text-gray-500">Optional (comma-separated names)</span>
                      </div>
                    </div>
                    
                    <div className="mt-4">
                      <button
                        type="button"
                        onClick={() => {
                          const csvContent = `ProductID,ProductName,Description,BrandName,CategoryName,MRP,OfferPrice,BusinessType
"IPH15PRO","iPhone 15 Pro","Latest iPhone with advanced features","Apple","Electronics",99999,89999,"Corporate,Retail"
"SAMGS24","Samsung Galaxy S24","Premium Android smartphone","Samsung","Electronics",89999,79999,"Corporate"
"MBAIRM2","MacBook Air M2","Lightweight laptop for professionals","Apple","Electronics",129999,119999,"Corporate,Enterprise"
"GIFTBAG","Premium Gift Bag","Elegant gift packaging","Generic","Non-Electronics",299,199,"Retail"`;
                          
                          const blob = new Blob([csvContent], { type: 'text/csv' });
                          const url = window.URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = 'products_template.csv';
                          document.body.appendChild(a);
                          a.click();
                          document.body.removeChild(a);
                          window.URL.revokeObjectURL(url);
                        }}
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                      >
                        Download Sample CSV
                      </button>
                    </div>
                  </div>
                  
                  <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <h5 className="text-sm font-medium text-yellow-800 mb-2">Important Notes:</h5>
                    <ul className="text-xs text-yellow-700 space-y-1">
                      <li>• Brand names must match exactly with existing brands in the database</li>
                      <li>• Category names must be either "Electronics" or "Non-Electronics" only</li>
                      <li>• Business Type names must match exactly with existing business types (comma-separated for multiple)</li>
                      <li>• Prices should be numbers only (no currency symbols)</li>
                      <li>• Product photos are not included in bulk upload</li>
                      <li>• All products will be set as Active by default</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Product Modal */}
      {showEditProductModal && (
        <div className="admin-modal-overlay">
          <div className="admin-modal max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                Edit Product
              </h3>
              <button
                onClick={handleCloseEditProductModal}
                className="text-gray-400 hover:text-gray-600 text-xl font-bold"
              >
                ×
              </button>
            </div>
            
            <form onSubmit={handleUpdateProduct} className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="editProductId" className="block text-sm font-medium text-gray-700 mb-1">
                    Product ID *
                  </label>
                  <input
                    type="text"
                    id="editProductId"
                    name="productId"
                    value={productForm.productId}
                    onChange={handleProductFormChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="Enter product ID"
                    required
                  />
                </div>
                
                <div>
                  <label htmlFor="editProductName" className="block text-sm font-medium text-gray-700 mb-1">
                    Product Name *
                  </label>
                  <input
                    type="text"
                    id="editProductName"
                    name="name"
                    value={productForm.name}
                    onChange={handleProductFormChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="Enter product name"
                    required
                  />
                </div>
                
                <div className="md:col-span-2">
                  <label htmlFor="editProductDescription" className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    id="editProductDescription"
                    name="description"
                    value={productForm.description}
                    onChange={handleProductFormChange}
                    rows="3"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="Enter product description"
                  />
                </div>
                
                <div>
                  <label htmlFor="editProductBrand" className="block text-sm font-medium text-gray-700 mb-1">
                    Brand *
                  </label>
                  <select
                    id="editProductBrand"
                    name="brandId"
                    value={productForm.brandId}
                    onChange={handleProductFormChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    required
                  >
                    <option value="">Select a brand</option>
                    {brands.filter(brand => brand.isActive).map((brand) => (
                      <option key={brand.id} value={brand.id}>
                        {brand.name}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label htmlFor="editProductCategory" className="block text-sm font-medium text-gray-700 mb-1">
                    Category *
                  </label>
                  <select
                    id="editProductCategory"
                    name="categoryId"
                    value={productForm.categoryId}
                    onChange={handleProductFormChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    required
                  >
                    <option value="">Select a category</option>
                    {categories.filter(category => category.isActive).map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="md:col-span-2">
                  <label className="flex items-center space-x-3 cursor-pointer p-3 border border-gray-300 rounded-lg hover:bg-green-50 transition-colors">
                    <input
                      type="checkbox"
                      checked={productForm.isPharma || false}
                      onChange={(e) => setProductForm(prev => ({ ...prev, isPharma: e.target.checked }))}
                      className="w-5 h-5 text-green-600 border-gray-300 rounded focus:ring-green-500"
                    />
                    <div>
                      <span className="text-sm font-medium text-gray-700">Pharma Product</span>
                      <p className="text-xs text-gray-500">Check this if the product is a pharmaceutical/healthcare item</p>
                    </div>
                  </label>
                </div>
                
                <div>
                  <label htmlFor="editProductMrp" className="block text-sm font-medium text-gray-700 mb-1">
                    MRP *
                  </label>
                  <input
                    type="number"
                    id="editProductMrp"
                    name="mrp"
                    value={productForm.mrp}
                    onChange={handleProductFormChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="Enter MRP"
                    min="0"
                    step="0.01"
                    required
                  />
                </div>
                
                <div>
                  <label htmlFor="editProductOfferPrice" className="block text-sm font-medium text-gray-700 mb-1">
                    Offer Price *
                  </label>
                  <input
                    type="number"
                    id="editProductOfferPrice"
                    name="offerPrice"
                    value={productForm.offerPrice}
                    onChange={handleProductFormChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="Enter offer price"
                    min="0"
                    step="0.01"
                    required
                  />
                </div>
                
                <div className="md:col-span-2">
                  <label htmlFor="editProductPhoto" className="block text-sm font-medium text-gray-700 mb-1">
                    Product Photos
                  </label>
                  
                  {/* Show current photos if editing */}
                  {editingProduct && (editingProduct.photos || editingProduct.photo) && (
                    <div className="mb-4">
                      <p className="text-sm font-medium text-gray-700 mb-2">Current Photos</p>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {(editingProduct.photos || [editingProduct.photo]).filter(Boolean).map((photo, index) => {
                          const isMarkedForRemoval = productForm.removeCurrentPhotos && productForm.removeCurrentPhotos.includes(index);
                          return (
                            <div key={index} className={`relative ${isMarkedForRemoval ? 'opacity-50' : ''}`}>
                              <div className="w-full h-24 rounded-lg overflow-hidden bg-gray-100">
                                <img
                                  src={photo}
                                  alt={`Current product photo ${index + 1}`}
                                  className="w-full h-full object-cover"
                                />
                              </div>
                              <button
                                type="button"
                                onClick={() => handleRemoveCurrentPhoto(index)}
                                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600 transition-colors"
                              >
                                ×
                              </button>
                              <p className="text-xs text-gray-500 mt-1">
                                {isMarkedForRemoval ? 'Will be removed' : `Current photo ${index + 1}`}
                              </p>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-orange-400 transition-colors">
                    <input
                      type="file"
                      id="editProductPhoto"
                      accept="image/*"
                      multiple
                      onChange={handleProductPhotoUpload}
                      className="hidden"
                    />
                    <label htmlFor="editProductPhoto" className="cursor-pointer">
                      <div>
                        <div className="w-16 h-16 mx-auto mb-2 rounded-lg bg-gray-100 flex items-center justify-center">
                          <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                          </svg>
                        </div>
                        <p className="text-sm text-gray-600">Click to upload new product photos</p>
                        <p className="text-xs text-gray-500">PNG, JPG up to 5MB each (multiple files allowed)</p>
                        <p className="text-xs text-orange-600 mt-1">Note: This will replace all current photos</p>
                      </div>
                    </label>
                  </div>
                  
                  {/* Display new uploaded photos */}
                  {productForm.photos && productForm.photos.length > 0 && (
                    <div className="mt-4">
                      <p className="text-sm font-medium text-gray-700 mb-2">New Photos to Upload ({productForm.photos.length})</p>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {productForm.photos.map((photo, index) => (
                          <div key={index} className="relative">
                            <div className="w-full h-24 rounded-lg overflow-hidden bg-gray-100">
                              <img
                                src={URL.createObjectURL(photo)}
                                alt={`New product photo ${index + 1}`}
                                className="w-full h-full object-cover"
                              />
                            </div>
                            <button
                              type="button"
                              onClick={() => handleRemoveProductPhoto(index)}
                              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600 transition-colors"
                            >
                              ×
                            </button>
                            <p className="text-xs text-gray-500 mt-1 truncate">{photo.name}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={handleCloseEditProductModal}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="admin-btn-primary disabled:opacity-60"
                >
                  {isSubmitting ? 'Updating...' : 'Update Product'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Product Modal */}
      {viewingProduct && (
        <div className="admin-modal-overlay p-3 sm:p-4">
          <div className="admin-modal w-[96vw] max-w-7xl h-[94vh] flex flex-col overflow-hidden">
            <div className="flex justify-between items-center px-6 py-4 border-b border-slate-200 shrink-0">
              <h3 className="text-lg font-display font-semibold text-slate-900">Product Details</h3>
              <button
                type="button"
                onClick={handleCloseViewProduct}
                className="p-2 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 min-h-0 p-5 sm:p-6 overflow-y-auto lg:overflow-hidden">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 lg:gap-8 h-full lg:min-h-0">
                <div className="lg:col-span-5 flex flex-col gap-3 lg:min-h-0">
                  {getProductPhotos(viewingProduct).length > 0 ? (
                    <>
                      <div className="h-56 sm:h-72 lg:h-[calc(94vh-14rem)] lg:min-h-[320px] overflow-hidden bg-slate-100 rounded-xl border border-slate-200">
                        <img
                          src={getProductPhotos(viewingProduct)[viewPhotoIndex]}
                          alt={viewingProduct.name}
                          className="w-full h-full object-contain p-4"
                        />
                      </div>
                      {getProductPhotos(viewingProduct).length > 1 && (
                        <div className="grid grid-cols-5 gap-2 shrink-0">
                          {getProductPhotos(viewingProduct).map((photo, index) => (
                            <button
                              key={index}
                              type="button"
                              onClick={() => setViewPhotoIndex(index)}
                              className={`aspect-square overflow-hidden rounded-lg border-2 transition-all ${
                                viewPhotoIndex === index
                                  ? 'border-primary-600 ring-2 ring-primary-200'
                                  : 'border-slate-200 hover:border-slate-300'
                              }`}
                            >
                              <img src={photo} alt="" className="w-full h-full object-cover" />
                            </button>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="h-56 sm:h-72 lg:h-[calc(94vh-14rem)] flex items-center justify-center bg-slate-100 rounded-xl border border-slate-200">
                      <CubeIcon className="w-16 h-16 text-slate-300" />
                    </div>
                  )}

                  <div className="flex flex-wrap gap-2 shrink-0">
                    {viewingProduct.brandName && (
                      <span className="px-3 py-1 bg-primary-100 text-primary-800 text-sm font-medium rounded-full">
                        {viewingProduct.brandName}
                      </span>
                    )}
                    {viewingProduct.categoryName && (
                      <span className="px-3 py-1 bg-slate-800 text-white text-sm font-medium rounded-full">
                        {viewingProduct.categoryName}
                      </span>
                    )}
                    {viewingProduct.isPharma && (
                      <span className="px-3 py-1 bg-emerald-100 text-emerald-800 text-sm font-medium rounded-full">
                        Pharma
                      </span>
                    )}
                  </div>
                </div>

                <div className="lg:col-span-7 flex flex-col gap-4 lg:min-h-0 lg:overflow-y-auto">
                  <div>
                    <h2 className="text-2xl lg:text-3xl font-display font-bold text-slate-900">{viewingProduct.name}</h2>
                    {viewingProduct.description && (
                      <p className="mt-2 text-slate-600 leading-relaxed text-sm sm:text-base">{viewingProduct.description}</p>
                    )}
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Product ID</p>
                      <p className="text-sm font-medium text-slate-900">{viewingProduct.productId || '—'}</p>
                    </div>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Status</p>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        viewingProduct.isActive
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {viewingProduct.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">MRP</p>
                      <p className="text-sm font-medium text-slate-900">{formatProductPrice(viewingProduct.mrp)}</p>
                    </div>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Offer Price</p>
                      <p className="text-sm font-bold text-emerald-600">{formatProductPrice(viewingProduct.offerPrice)}</p>
                    </div>
                  </div>

                  {viewingProduct.mrp && viewingProduct.offerPrice && viewingProduct.mrp > viewingProduct.offerPrice && (
                    <p className="text-sm text-emerald-600 font-medium -mt-2">
                      Save {formatProductPrice(viewingProduct.mrp - viewingProduct.offerPrice)} (
                      {Math.round(((viewingProduct.mrp - viewingProduct.offerPrice) / viewingProduct.mrp) * 100)}% off)
                    </p>
                  )}

                  {viewingProduct.businessTypeNames?.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Business Types</p>
                      <div className="flex flex-wrap gap-2">
                        {viewingProduct.businessTypeNames.map((type) => (
                          <span key={type} className="px-2.5 py-1 bg-slate-100 text-slate-700 text-xs rounded-lg">
                            {type}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {viewingProduct.features?.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Features</p>
                      <div className="flex flex-wrap gap-2">
                        {viewingProduct.features.map((feature, index) => (
                          <span key={index} className="px-2.5 py-1 bg-slate-100 text-slate-700 text-sm rounded-full">
                            {feature}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-200 shrink-0 bg-white">
              <button type="button" onClick={handleCloseViewProduct} className="admin-btn-secondary">
                Close
              </button>
              <button
                type="button"
                onClick={() => {
                  handleCloseViewProduct();
                  handleEditProduct(viewingProduct);
                }}
                className="admin-btn-primary"
              >
                Edit Product
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default AdminDashboard;
